type Key = string | number;

export type BusinessPlanGroup = "plan" | "actual" | "contractor";

export type BusinessPlanTreeNode = {
  configId: string;
  children?: BusinessPlanTreeNode[];
  note?: string;
  [key: string]: any;
};

type ChangeItem = {
  note: string;
  configId: string;
  group: BusinessPlanGroup;
  value: number;
};

type ChangesPayload = { changes: ChangeItem[] };
type AnyObj = Record<string, any>;

export type BusinessPlanPaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

export function resetBusinessPlanPaginationAfterCriteriaChange(
  pagination: BusinessPlanPaginationState,
): BusinessPlanPaginationState {
  return {
    ...pagination,
    current: 1,
  };
}

export function canDeleteBusinessPlan(
  state: string | undefined,
  hasDeletePermission: boolean,
): boolean {
  return state === "CREATED" && hasDeletePermission;
}

export function applyBusinessPlanInvalidation<T extends AnyObj>(
  rows: T[],
  id: string,
  response: T,
  activeTab: string,
): T[] {
  if (activeTab === "CREATED") {
    return rows.filter((row) => row._id !== id);
  }

  return rows.map((row) => (row._id === id ? response : row));
}

export function getBusinessPlanPaginationAfterInvalidation<
  T extends { current: number },
>(pagination: T, activeTab: string, currentPageRowCount: number): T {
  if (
    activeTab !== "CREATED" ||
    pagination.current <= 1 ||
    currentPageRowCount > 1
  ) {
    return pagination;
  }

  return {
    ...pagination,
    current: pagination.current - 1,
  };
}

export function createBusinessPlanDraft(
  source: AnyObj,
  temporaryId: string,
): AnyObj {
  return {
    _id: temporaryId,
    code: "",
    customer: source.customer,
    customerName: source.customerName,
    routeBase: source.routeBase,
    routeBaseCode: source.routeBaseCode,
    ownership: source.ownership,
    validFrom: source.validFrom,
    roadType: source.roadType,
    direction: source.direction,
    numbersOfDriver: source.numbersOfDriver,
    container: source.container,
    receiptAddress: source.receiptAddress,
    deliveryAddress: source.deliveryAddress,
    revenue: source.revenue,
    profit: { plan: 0, contractor: 0 },
    status: "new",
    state: "CREATED",
    lines: [],
  };
}

export function replaceBusinessPlanRowById<T extends AnyObj>(
  rows: T[],
  rowId: string | number,
  savedRow: T,
): T[] {
  return rows.map((row) => (row._id === rowId ? savedRow : row));
}

export function applyBusinessPlanRouteDetail<T extends AnyObj>(
  row: T,
  routeSelection: any,
  routeDetail: AnyObj,
): T {
  return {
    ...row,
    routeBaseCode: routeSelection,
    routeBase: routeDetail,
    roadType: routeDetail.roadType,
    direction: routeDetail.direction,
    receiptAddress: routeDetail.receipt?.address,
    deliveryAddress: routeDetail.delivery?.address,
  };
}

export function mergeBusinessPlanRowsWithEdits<T extends AnyObj>(
  fetchedRows: T[],
  editingRows: Array<{ record?: T }>,
): T[] {
  const editedRecords = editingRows
    .map((item) => item.record)
    .filter((record): record is T => Boolean(record));
  const editingIds = new Set(editedRecords.map((record) => String(record._id)));

  return [
    ...editedRecords,
    ...fetchedRows.filter((row) => !editingIds.has(String(row._id))),
  ];
}

export function tryAcquireBusinessPlanSaveLock(
  activeRowIds: Set<string>,
  rowId: string,
): boolean {
  if (activeRowIds.has(rowId)) return false;
  activeRowIds.add(rowId);
  return true;
}

export function releaseBusinessPlanSaveLock(
  activeRowIds: Set<string>,
  rowId: string,
) {
  activeRowIds.delete(rowId);
}

export function shouldApplyBusinessPlanListResponse(
  requestId: number,
  latestRequestId: number,
  requestMutationVersion: number,
  currentMutationVersion: number,
): boolean {
  return (
    requestId === latestRequestId &&
    requestMutationVersion === currentMutationVersion
  );
}

export {
  hasNullRequiredField,
  normalizeTouchedKey,
  setDeepImmutable,
  toPath,
} from "../../shared/helpers/objectPath";

const isEmptyValue = (value: any) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

export function setByPath<T extends object>(obj: any, path: Key[], value: any) {
  if (!path.length) return obj;

  const [head, ...rest] = path;
  const next = Array.isArray(obj) ? [...obj] : { ...(obj ?? {}) };

  if (rest.length === 0) {
    next[head] = value;
    return next;
  }

  next[head] = setByPath(next[head], rest, value);
  return next;
}

export function updateTreeById(
  nodes: BusinessPlanTreeNode[] = [],
  targetId: string | number,
  updater: (node: BusinessPlanTreeNode) => BusinessPlanTreeNode,
): BusinessPlanTreeNode[] {
  return nodes.map((node) => {
    if (String(node.configId) === String(targetId)) return updater(node);
    if (node.children?.length) {
      return {
        ...node,
        children: updateTreeById(node.children, targetId, updater),
      };
    }
    return node;
  });
}

export function mapAllEditableNodesToPayload(
  tree: BusinessPlanTreeNode[],
  groups: BusinessPlanGroup[] = ["plan", "actual", "contractor"],
  skipLocked = false,
): ChangesPayload {
  const changes: ChangeItem[] = [];

  const walk = (nodes: BusinessPlanTreeNode[]) => {
    for (const node of nodes) {
      for (const group of groups) {
        const cell = node.values?.[group];
        if (!cell || !cell.editable) continue;
        if (skipLocked && cell.locked) continue;

        changes.push({
          note: (node.note ?? "").trim(),
          configId: node.configId,
          group,
          value: Number(cell.value ?? 0),
        });
      }

      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(tree);
  return { changes };
}

export function mergePriorityDeep<T extends AnyObj>(
  obj1: T,
  obj2: Partial<T>,
): T {
  if (isEmptyValue(obj1)) return obj2 as T;
  if (isEmptyValue(obj2)) return obj1;

  const isObj1Plain = typeof obj1 === "object" && !Array.isArray(obj1);
  const isObj2Plain = typeof obj2 === "object" && !Array.isArray(obj2);
  if (!isObj1Plain || !isObj2Plain) {
    return (isEmptyValue(obj1) ? (obj2 as any) : (obj1 as any)) as T;
  }

  const result: AnyObj = {};
  const keys = new Set([
    ...Object.keys(obj2 || {}),
    ...Object.keys(obj1 || {}),
  ]);

  keys.forEach((key) => {
    const value1 = (obj1 as AnyObj)[key];
    const value2 = (obj2 as AnyObj)[key];
    const bothPlainObjects =
      value1 &&
      value2 &&
      typeof value1 === "object" &&
      typeof value2 === "object" &&
      !Array.isArray(value1) &&
      !Array.isArray(value2);

    if (bothPlainObjects) {
      result[key] = mergePriorityDeep(value1, value2);
      return;
    }

    result[key] = isEmptyValue(value1) ? value2 : value1;
  });

  return result as T;
}
